/**
 * Issue #765 — "grounds near you" (wave 5, slice 1). Pure arithmetic and
 * copy for the redrawn `/grounds-map`, split out for the same two reasons
 * #643/#644/#683 split `directoryEnrich.js`/`directorySearch.js`/
 * `mapViewport.js` out of `GroundsMap.jsx`: this repo mounts no components
 * in unit tests (see `GroundsMap.test.js`'s own header), and a component
 * function measured against `max-lines-per-function` has no room to grow
 * five more list-shaping helpers.
 *
 * Matched against `GroundsNearYou.dc.html` (round 5, sha256 6e4e509a…) and
 * brief-765.md. D120 (decisions-2026-09-24.md) leaves the "(#689)"/"(#713)"
 * annotations out of two copy strings below.
 */
import { FACILITY_SURFACES, FACILITY_SURFACE_META } from "./facilities";

/**
 * #800's own decision ("A redrawn screen never hardcodes a cross-screen
 * route. It asks a helper" — `lib/coachSetup.js`'s `COACH_SETUP_PATH` is
 * the pattern's first instance): `/grounds-map` is flag-invariant (the
 * SAME url on both legs of `newUi("grounds")` — the flag only decides
 * which component renders there), so no resolver function is needed the
 * way a genuinely different-per-flag destination would need one. Routing
 * every OTHER file's link through this constant still matters, because a
 * literal `to="/grounds-map"` and this constant are not equally safe to
 * `flagSafeLinks.test.js`'s own static scan: a raw JSX `to="…"` literal
 * trips BOTH its generic literal pass and its syntax-aware to/href pass on
 * one line (by design, fix round 5 K2/K3 — no de-duplication), which an
 * allowlist entry cannot cover 1:1 the way an imported-constant USE
 * already can (`CoachSetupGate.jsx`'s own two entries).
 */
export const GROUNDS_MAP_PATH = "/grounds-map";

/** D120 — the drawing's own helper line, minus its "(#689)" annotation. */
export const GROUNDS_PLAYED_HELPER =
  "Yours first, then nearby. A to Z, so the rail always points somewhere; the search line above narrows this list rather than the world.";

/**
 * #1232 (D247) — the one line a player with 0 played grounds sees in place of
 * the played section. The round-5 board draws no empty state, so this is the
 * issue's own fallback wording.
 */
export const GROUNDS_PLAYED_EMPTY = "Grounds you play at will show here.";

/**
 * #1288 (round-12 CoachGrounds, D298) — the coach's /grounds-map. A coach logs
 * no matches, so the list a coach keeps is the grounds they added.
 */
export const COACH_ADDED_EMPTY = "Grounds you add will show here.";
export const COACH_FIND_TITLE = "Find a ground";
/** A ground whose fields and nets nobody has recorded: the facts line says so. */
export const FACTS_NONE_RECORDED = "Fields and nets not recorded";
export const COACH_NO_MATCH_HINT = "Try a shorter word, or add the ground.";

/** `Nothing matches “term”.` with curly quotes, as the board draws it. */
export function coachNoMatchLine(term) {
  return `Nothing matches \u201C${(term || "").trim()}\u201D.`;
}

/** `Grounds you added · 2`, the header over the coach's own grounds. */
export function coachAddedHeader(count, truncated = false) {
  // The read is capped; past the cap the count is a floor, not a total.
  return `Grounds you added \u00B7 ${truncated ? `${count.toLocaleString("en-NZ")}+` : count}`;
}

/** The coach's one-ground facts line, or the not-recorded sentence. */
export function coachFactsLine({ placeType, facilities } = {}) {
  return groundFactsLine({ placeType, facilities })?.line || FACTS_NONE_RECORDED;
}

/** The coach card's note over Correct details (D301 Q4; S3's recommendation). */
export const COACH_CARD_NOTE = "You haven't logged a match here, so there are no figures of yours.";
export const COACH_CARD_NOTE_EDIT = "A ground you added can be corrected:";

/**
 * A Nearby row's distance. D339 (board GroundsNearDistance, D259): under a
 * kilometre it reads in kilometres to one decimal ("0.6 km"), never metres;
 * from 1 km it is the shared formatter's label. `_km` is what decides the
 * format: a row with no `_km` (none today: `nearestGrounds` and `addGround` set
 * both) falls back to its `_distanceLabel`, and with neither it is "".
 */
export function nearbyDistanceLabel(row) {
  const km = row?._km;
  if (typeof km === "number" && Number.isFinite(km) && km >= 0 && km < 1) return `${km.toFixed(1)} km`;
  return row?._distanceLabel || "";
}

/**
 * #1232 (D247.1, orchestrator ruling, open to Andreas's veto) — the A-Z rail
 * shows only when the list it indexes holds MORE than this many rows, played
 * and nearby counted together. At 10 or fewer a player sees everything
 * without jumping and the rail is noise.
 */
export const RAIL_MAX_ROWS_WITHOUT = 10;

/** True when `rowCount` (played + nearby) is long enough to need the rail. */
export function railNeeded(rowCount) {
  return rowCount > RAIL_MAX_ROWS_WITHOUT;
}

/** D120 — the drawing's own "somewhere else" helper, minus its "(#713)" annotation. */
export const ADD_ELSEWHERE_HELPER =
  "One line: the name, and where you are standing. No coordinates, no second window. Fields and nets come later, from the ground card.";

export const NOTHING_NEAR_TITLE = "No known grounds within 50 km of you.";
export const NOTHING_NEAR_ACTION = "Add this place";
export const DIRECTORY_FAILED_TITLE = "Couldn't load the grounds";
export const DIRECTORY_FAILED_BODY = "Nothing has been lost — we just couldn't reach the server.";

/** A–Z, once, module level — the rail's own alphabet. */
export const ALPHABET = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

/**
 * "Played once" / "Played twice" / "Played N times" — `GroundsNearYou.
 * dc.html:106,117,128,135` draws exactly these three shapes (never "Played
 * 3 times" read as "thrice"). Distinct from `lib/logGroundStep.js`'s
 * `playedAtLabel` ("Played here N times") — a different screen's own
 * wording, not a shared constant, per the drawing's verbatim copy rule.
 */
export function playedCountLabel(matches) {
  const n = Number(matches) || 0;
  if (n === 1) return "Played once";
  if (n === 2) return "Played twice";
  return `Played ${n} times`;
}

/**
 * The distinct surfaces recorded across a set of facility rows, in
 * `FACILITY_SURFACES` vocabulary order, lower-cased for the facts line
 * (`GroundsNearYou.dc.html` draws "turf"/"synthetic", not "Turf"/
 * "Synthetic" — sentence-level copy, not a chip label).
 */
function distinctSurfaceWords(rows) {
  return FACILITY_SURFACES.filter((s) => rows.some((f) => f.surface === s)).map((s) =>
    FACILITY_SURFACE_META[s].label.toLowerCase(),
  );
}

/**
 * The played-list facts line — `GroundsNearYou.dc.html:108,119,130,137,148,159`.
 *
 * R-005 or, spelled out for this screen: a fact the record does not hold is
 * NAMED as missing, never defaulted to zero. `ground_facilities` carries no
 * `unknown` member (`lib/facilities.js`'s own header) — a kind this venue's
 * rows never mention is indistinguishable from "definitely none", so BOTH
 * read as "not recorded" here, uniformly, rather than guessing which of the
 * drawing's two examples (`Played twice`'s bare "1 field · turf" vs `Played
 * 5 times`'s "1 field · turf · nets not recorded", both otherwise identical
 * one-field-turf rows) was the rule and which was the artist's fixture
 * variety. Consistency with the acceptance criterion ("no row prints `0
 * nets` or `0 fields`") is preferred over matching one pixel example over
 * the other.
 *
 * `placeType === "indoor"` swaps the FIELD segment for "indoor centre"
 * (`GroundsNearYou.dc.html:148` — "indoor centre · 6 nets · synthetic"):
 * an indoor centre's own facilities are its nets, not a field count, so
 * there is nothing to report as "fields not recorded" once the place type
 * already says what kind of venue this is.
 *
 * Returns `null` when nothing at all is recorded for this ground AND it is
 * not an indoor centre — the same "nothing to say" rule `facilitySummaryLabel`
 * follows in `lib/facilities.js`.
 */
export function groundFactsLine({ placeType, facilities } = {}) {
  const rows = (facilities || []).filter(Boolean);
  const fields = rows.filter((f) => f.kind === "field");
  const nets = rows.filter((f) => f.kind === "net");
  const indoor = placeType === "indoor";

  if (rows.length === 0 && !indoor) return null;

  const parts = [];
  const notes = [];

  if (indoor) {
    parts.push("indoor centre");
  } else if (fields.length > 0) {
    parts.push(`${fields.length} field${fields.length === 1 ? "" : "s"}`);
  } else {
    notes.push("fields not recorded");
  }

  if (nets.length > 0) {
    parts.push(`${nets.length} net${nets.length === 1 ? "" : "s"}`);
  } else {
    // Nets are always worth naming as missing, indoor centre or not — an
    // indoor centre with no recorded nets is exactly the case a reader most
    // wants flagged, not the one most safe to drop.
    notes.push("nets not recorded");
  }

  parts.push(...distinctSurfaceWords(rows));

  if (rows.some((f) => f.has_lighting)) parts.push("floodlights");

  return {
    line: parts.join(" · "),
    // The muted trailing segment(s) — `GroundsNearYou.dc.html:137,159` draws
    // this as a SEPARATE, lighter span, joined with its own leading " · ".
    notes: notes.map((n) => `· ${n}`),
  };
}

/**
 * The "Nearby" card's fact chips — `GroundsNearYou.dc.html:179-181,190-191`.
 * Unlike the played facts LINE, an absent count here is simply omitted
 * (never noted) — the drawing's two examples agree on that: row one has no
 * "0 fields" chip and no "nets not recorded" chip, it just has none. Only
 * the SURFACE gets an explicit "not recorded" chip when nothing is known,
 * because the drawing's second card draws exactly that
 * ("Surface not recorded", dashed border) rather than omitting the chip
 * outright.
 */
export function nearbyFactChips({ placeType, facilities } = {}) {
  const rows = (facilities || []).filter(Boolean);
  const fields = rows.filter((f) => f.kind === "field");
  const nets = rows.filter((f) => f.kind === "net");
  const indoor = placeType === "indoor";
  const chips = [];

  if (indoor) chips.push({ label: "Indoor centre", muted: false });
  else if (fields.length > 0) chips.push({ label: `${fields.length} field${fields.length === 1 ? "" : "s"}`, muted: false });

  if (nets.length > 0) chips.push({ label: `${nets.length} net${nets.length === 1 ? "" : "s"}`, muted: false });

  const surfaces = distinctSurfaceWords(rows);
  if (surfaces.length > 0) {
    surfaces.forEach((s) => chips.push({ label: s[0].toUpperCase() + s.slice(1), muted: false }));
  } else {
    chips.push({ label: "Surface not recorded", muted: true });
  }

  if (rows.some((f) => f.has_lighting)) chips.push({ label: "Floodlights", muted: false });

  return chips;
}

/**
 * D165 (decisions-2026-09-24.md, orchestrator ruling on the #903/#908
 * review, 2026-09-26) — the signed-off drawing draws the rail as the full
 * A-Z, always, with no "#": a name starting with a digit or symbol has no
 * first LETTER at all, so it is not a group the rail can index — it sorts
 * to the end of the played list instead, per the ruling's own words.
 *
 * The rail's earlier #903 fix (indexing only the groups present, `#`
 * included) is exactly what D165 overrides: the signed-off baseline wins
 * over the issue's own ask. `#`-scoped naming stays out of every function
 * below on purpose — there is no group by that name any more.
 */

/** The A-Z first letter of a ground's name, or `null` when it starts with
 * a digit, a symbol, or nothing at all — D165's own "no rail entry"
 * case. */
function firstLetterOrNull(name) {
  const ch = (name || "").trim().charAt(0).toUpperCase();
  return /[A-Z]/.test(ch) ? ch : null;
}

/**
 * D165 — A-Z first, then anything with no real first letter, each half
 * in its own A-Z order. Distinct from `sortByNameAZ` (below), which
 * every OTHER A-Z list in this codebase — `GroundPicker.jsx` among them —
 * still uses unchanged: a digit/symbol name sorts in its ordinary locale
 * position there. Only THIS screen's played list, and the rail built from
 * it, need the unlettered tail kept separate.
 */
export function sortPlayedForRail(list) {
  const lettered = [];
  const unlettered = [];
  for (const g of list) {
    (firstLetterOrNull(g.name) ? lettered : unlettered).push(g);
  }
  return [...sortByNameAZ(lettered), ...sortByNameAZ(unlettered)];
}

/**
 * Groups an already-`sortPlayedForRail`-sorted played list by its first
 * letter. `unlettered` (D165: digit/symbol names) is returned separately
 * — the row list renders it as a plain, headerless tail, never a "#"
 * section.
 */
export function groupPlayedByLetter(playedGrounds) {
  const groups = new Map();
  const unlettered = [];
  for (const g of playedGrounds) {
    const letter = firstLetterOrNull(g.name);
    if (!letter) {
      unlettered.push(g);
      continue;
    }
    if (!groups.has(letter)) groups.set(letter, []);
    groups.get(letter).push(g);
  }
  return { groups, unlettered };
}

/**
 * D165 / review round 3 (F4) — the rail's own 26 entries, `has: true`
 * when EITHER a played OR a nearby row starts with that letter (the rail
 * "indexes every row the list shows, played and nearby" — the ruling's
 * own words). Always all 26; never a 27th "#" entry.
 */
export function railLetterIndex(playedRows, nearbyRows = []) {
  const present = new Set();
  for (const g of playedRows) {
    const letter = firstLetterOrNull(g.name);
    if (letter) present.add(letter);
  }
  for (const g of nearbyRows) {
    const letter = firstLetterOrNull(g.name);
    if (letter) present.add(letter);
  }
  return ALPHABET.map((letter) => ({ letter, has: present.has(letter) }));
}

/**
 * D165 — "a letter with no grounds is muted but still active, and tapping
 * it jumps to the next group that exists": scans FORWARD (never wraps)
 * from `letter` (inclusive — a present letter simply resolves to itself)
 * to the next entry in `rail` with `has: true`. `null` when nothing from
 * `letter` onward has anything.
 */
export function nextPresentLetter(letter, rail) {
  const idx = rail.findIndex((r) => r.letter === letter);
  if (idx === -1) return null;
  for (let i = idx; i < rail.length; i += 1) {
    if (rail[i].has) return rail[i].letter;
  }
  return null;
}

/** A-Z, case-insensitive, diacritic-blind — same comparator every A-Z list
 * in this codebase uses via `localeCompare`. */
export function sortByNameAZ(list) {
  return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
}

/**
 * Issue #765's own rendering cap, PR #858 review round 1 (F1) — the
 * drawing itself (`GroundsNearYou.dc.html`) draws exactly SIX rows before
 * its own "35 more, S to W" footer, over a 41-ground fixture: M, N, O, O,
 * P, S. 6, not a round number chosen for convenience — matched to what the
 * artboard actually shows, so a 41-ground fixture shaped like the drawing's
 * own reproduces its own "35 more, S to W" literally (41 - 6 = 35).
 */
export const PLAYED_LIST_VISIBLE_CAP = 6;

/**
 * "N more, S to W" — `GroundsNearYou.dc.html:166`. `null` when the whole
 * list fits inside the cap.
 */
export function moreFooter(sortedPlayed, cap = PLAYED_LIST_VISIBLE_CAP) {
  if (sortedPlayed.length <= cap) return null;
  const remaining = sortedPlayed.slice(cap);
  const startLetter = (remaining[0]?.name || "").trim().charAt(0).toUpperCase() || "?";
  const endLetter = (sortedPlayed[sortedPlayed.length - 1]?.name || "").trim().charAt(0).toUpperCase() || "?";
  return `${remaining.length} more, ${startLetter} to ${endLetter}`;
}

/**
 * Search narrows the PLAYED list only (`GroundsNearYou.dc.html:95`: "the
 * search line above narrows this list rather than the world") — a
 * plain case-insensitive substring match over the name, the town and the
 * country, same fields `lib/directorySearch.js`'s own client-side
 * `matchesSearchTerm` compares (kept independent here rather than imported:
 * that function's contract is the SERVER-paged directory search, and this
 * one only ever runs over a list already fully in memory).
 */
export function narrowPlayedByTerm(playedGrounds, term) {
  const q = (term || "").trim().toLowerCase();
  if (!q) return playedGrounds;
  return playedGrounds.filter((g) => {
    const hay = [g.name, g.city, g.country].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });
}

/** #1143: the line the Grounds screen shows once an add step has saved. */
export function groundSavedLine(name) {
  return `${name} is saved to your grounds.`;
}
